import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, CONTACT, formatXCD, STATUS_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, ArrowLeft, CheckCircle2, Clock, Share2, Mail, Copy, MessageCircle } from "lucide-react";

const SERVICE_LABELS = {
  water: "Water Delivery", bin: "Bin Rental", diagnostics: "Diagnostics",
  trucking: "Trucking", rental: "Vehicle Rental",
};

function lineItems(b) {
  if (!b) return [];
  if (b.service === "water") {
    const items = [];
    const label = b.water_label || `Water delivery — ${b.gallons || ""} gallons`;
    const base = Number(b.base_price ?? b.price ?? 0);
    items.push({ desc: label, qty: 1, unit: base, total: base });
    if (Number(b.hill_surcharge || 0) > 0) {
      items.push({
        desc: `Hill surcharge${b.hill_area_name ? ` — ${b.hill_area_name}` : ""}`,
        qty: 1, unit: Number(b.hill_surcharge), total: Number(b.hill_surcharge),
      });
    }
    if (b.after_hours && Number(b.after_hours_fee || 0) > 0) {
      items.push({
        desc: "After-hours fee",
        qty: 1, unit: Number(b.after_hours_fee), total: Number(b.after_hours_fee),
      });
    }
    return items;
  }
  if (b.service === "bin") return [{ desc: `Bin rental (${b.duration_days} days)`, qty: 1, unit: b.price, total: b.price }];
  if (b.service === "diagnostics") return [{ desc: `${b.vehicle_type === "truck" ? "Truck" : "Car"} diagnostics — ${b.vehicle_make || ""} ${b.vehicle_model || ""}`, qty: 1, unit: b.price, total: b.price }];
  if (b.service === "rental") return [{ desc: `${b.vehicle_label} · ${b.days} days`, qty: b.days, unit: b.price / Math.max(b.days, 1), total: b.price }];
  if (b.service === "trucking") return [{ desc: `Trucking quote — ${b.material_type} · ${b.quantity}`, qty: 1, unit: b.price || 0, total: b.price || 0 }];
  return [{ desc: SERVICE_LABELS[b.service] || b.service, qty: 1, unit: b.price, total: b.price }];
}

/**
 * Dual-mode invoice page:
 *   /invoice/:id            — authenticated owner / admin view
 *   /i/:token               — public shareable view (no auth required)
 */
export default function InvoicePage() {
  const { id, token } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [b, setB] = useState(null);
  const [company, setCompany] = useState(null);
  const [err, setErr] = useState("");
  const printRef = useRef(null);

  const isPublic = location.pathname.startsWith("/i/");

  useEffect(() => {
    if (isPublic) {
      api.get(`/invoices/share/${token}`)
        .then(r => { setB(r.data.booking); setCompany(r.data.company); })
        .catch(e => setErr(e?.response?.data?.detail || "Invoice not found"));
      return;
    }
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get(`/bookings/${id}`).then(r => setB(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [id, token, isPublic, user, loading, nav]);

  const shareUrl = useMemo(() => {
    if (!b?.share_token) return "";
    return `${window.location.origin}/i/${b.share_token}`;
  }, [b]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-600">{err}</div>;
  if (!b) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading invoice…</div>;

  const items = lineItems(b);
  const subtotal = items.reduce((s, i) => s + (i.total || 0), 0);
  const isPaid = b.status === "completed";
  const docKind = isPaid ? "RECEIPT" : "INVOICE";

  const copyLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed — long-press the link instead"); }
  };

  const whatsappShare = () => {
    if (!shareUrl) return;
    const msg = `Hi ${b.customer_name || ""}, here's your ${docKind.toLowerCase()} ${b.invoice_number} from Cleavie's Enterprise — EC$${Number(b.price || subtotal).toFixed(2)}: ${shareUrl}`;
    // If customer phone exists, open chat directly; else fall back to generic share
    const phone = (b.customer_phone || "").replace(/[^0-9]/g, "");
    const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  const emailShare = async () => {
    // Admin re-sends via server (uses verified sender). Non-admin falls back to mailto.
    const isAdminLike = (user?.roles || []).some(r => ["admin", "dispatcher"].includes(r));
    if (!isPublic && isAdminLike) {
      try {
        await api.post(`/admin/bookings/${b.id}/send-invoice`);
        toast.success("Invoice emailed to customer");
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
      return;
    }
    const subject = encodeURIComponent(`${docKind} ${b.invoice_number} — Cleavie's Enterprise`);
    const body = encodeURIComponent(`Hi ${b.customer_name || ""},\n\nHere's your ${docKind.toLowerCase()}: ${shareUrl}\n\nAmount: EC$${Number(b.price || subtotal).toFixed(2)}\n\nThank you,\nCleavie's Enterprise`);
    window.location.href = `mailto:${b.customer_email || ""}?subject=${subject}&body=${body}`;
  };

  const companyInfo = company || { name: "Cleavie's Enterprise", address: CONTACT.address, phone: CONTACT.phone, email: CONTACT.email };

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 12mm; }
        }
      `}</style>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 no-print">
          {isPublic ? (
            <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-slate-700 hover:text-slate-900" data-testid="invoice-back-home">
              <ArrowLeft className="h-4 w-4" /> Cleavie's Enterprise
            </Link>
          ) : (
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm font-bold text-slate-700 hover:text-slate-900" data-testid="invoice-back">
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={copyLink} variant="outline" size="sm" data-testid="invoice-copy-btn">
              <Copy className="h-4 w-4 mr-1" /> Copy link
            </Button>
            <Button onClick={whatsappShare} variant="outline" size="sm" data-testid="invoice-whatsapp-btn"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
            </Button>
            <Button onClick={emailShare} variant="outline" size="sm" data-testid="invoice-email-btn">
              <Mail className="h-4 w-4 mr-1" /> Email
            </Button>
            <Button onClick={() => window.print()} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold" data-testid="invoice-print-btn">
              <Printer className="h-4 w-4 mr-1" /> Print / Save PDF
            </Button>
          </div>
        </div>

        <div ref={printRef} className="bg-white border border-slate-200 rounded-md p-8 sm:p-10 print:border-0 print:rounded-none print:p-0">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Cleavie's Enterprise" className="h-16 w-16 object-contain" />
              <div>
                <div className="font-heading font-black text-xl text-slate-900">CLEAVIE'S ENTERPRISE</div>
                <div className="text-xs text-slate-500">{companyInfo.address}</div>
                <div className="text-xs text-slate-500">{companyInfo.phone || CONTACT.phone} · {companyInfo.email || CONTACT.email}</div>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-xs font-black tracking-[0.2em] ${isPaid ? "text-emerald-700" : "text-[#E32636]"}`}>{docKind}</div>
              <div className="font-heading font-black text-2xl text-slate-900 mt-1">{b.invoice_number}</div>
              <div className="text-xs text-slate-500 mt-1">Issued {(b.created_at || "").slice(0, 10)}</div>
              <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
                {isPaid ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Clock className="h-3.5 w-3.5 text-amber-500" />}
                {STATUS_LABELS[b.status] || b.status}
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Bill to</div>
              <div className="mt-1 font-bold text-slate-900">{b.customer_name}</div>
              <div className="text-sm text-slate-600">{b.customer_email}</div>
              <div className="text-sm text-slate-600">{b.customer_phone || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Service details</div>
              <div className="mt-1 text-sm text-slate-700">
                <div><b>Service:</b> {SERVICE_LABELS[b.service] || b.service}</div>
                {b.delivery_date && <div><b>Date:</b> {b.delivery_date} {b.delivery_time}</div>}
                {b.rental_date && <div><b>Date:</b> {b.rental_date}</div>}
                {b.appointment_date && <div><b>Date:</b> {b.appointment_date}</div>}
                {b.pickup_date && <div><b>Pickup:</b> {b.pickup_date} → {b.return_date}</div>}
                {b.address && <div><b>Address:</b> {b.address}</div>}
                {b.pickup_location && <div><b>Pickup:</b> {b.pickup_location} → {b.delivery_location}</div>}
              </div>
            </div>
          </div>

          {/* Items */}
          <table className="w-full mt-8 text-sm">
            <thead>
              <tr className="border-b-2 border-slate-900 text-left">
                <th className="py-2 font-heading font-bold">Description</th>
                <th className="py-2 font-heading font-bold text-right w-20">Qty</th>
                <th className="py-2 font-heading font-bold text-right w-28">Unit (EC$)</th>
                <th className="py-2 font-heading font-bold text-right w-28">Total (EC$)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 text-slate-800">{it.desc}</td>
                  <td className="py-3 text-right text-slate-700">{it.qty}</td>
                  <td className="py-3 text-right text-slate-700">{Number(it.unit || 0).toFixed(2)}</td>
                  <td className="py-3 text-right font-bold text-slate-900">{Number(it.total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mt-6">
            <div className="w-full sm:w-72">
              <div className="flex justify-between py-1 text-sm text-slate-600">
                <span>Subtotal</span><span>EC${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1 text-sm text-slate-600">
                <span>Tax</span><span>EC$0.00</span>
              </div>
              <div className="flex justify-between py-3 mt-2 border-t-2 border-slate-900">
                <span className="font-heading font-black text-slate-900">TOTAL</span>
                <span className="font-heading font-black text-slate-900 text-xl" data-testid="invoice-total">{formatXCD(subtotal)}</span>
              </div>
              {isPaid && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md py-2 px-3 mt-3 text-center">
                  <div className="text-xs font-black tracking-wider text-emerald-800 uppercase">Paid · Thank you</div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500">
            <div className="font-bold text-slate-700">Notes</div>
            <div className="mt-1">{b.notes || b.admin_notes || "Thank you for choosing Cleavie's Enterprise."}</div>
            <div className="mt-4">
              Payment accepted: Cash · Bank Transfer · Credit/Debit Card. Reference invoice number on all payments.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
